/**
 * Admin Deposits Contracts
 *
 * DATABASE-FIRST: All Zod schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-08
 *
 * Endpoints covered:
 * - GET /api/admin/deposits - List deposits with filter, stats
 * - GET /api/admin/deposits/:referenceNumber - Deposit detail (full)
 * - POST /api/admin/deposits/:referenceNumber/force-complete - Force complete
 * - POST /api/admin/deposits/:referenceNumber/sync - Sync with provider
 * - PUT /api/admin/deposits/:referenceNumber/edit - Edit financials
 */
import { z } from 'zod';
import { depositStatusSchema, bigintString } from '../common.js';
// =============================================================================
// ADMIN DEPOSIT SUMMARY
// Used in list view: GET /api/admin/deposits
// =============================================================================
/**
 * Individual deposit in admin list view
 * Maps from deposits table + users.username/first_name join + payment_services.name join
 *
 * DB columns: reference_number (PK), user_telegram_id, amount, bonus_amount,
 * status, provider_hash, created_at, completed_at
 */
export const adminDepositSummarySchema = z.object({
    /** Reference number: DEP-YYYYMMDD-NNNNN (used as primary identifier) */
    referenceNumber: z.string(),
    /** User's Telegram ID (bigint as string) */
    telegramId: bigintString,
    /** User's Telegram username (nullable) */
    username: z.string().nullable(),
    /** User's first name (nullable) */
    firstName: z.string().nullable(),
    /** Deposit amount in local currency */
    amount: z.number().int(),
    /** Bonus amount credited (nullable, default 0) */
    bonusAmount: z.number().int().nullable(),
    /** Payment provider display name (nullable before cascade starts) */
    paymentMethod: z.string().nullable(),
    /** Deposit status: pending | paid | completed | failed | cancelled */
    status: depositStatusSchema,
    /** ISO timestamp when deposit was created */
    createdAt: z.string(),
    /** ISO timestamp when completed (nullable if not completed) */
    completedAt: z.string().nullable(),
});
// =============================================================================
// DEPOSITS LIST RESPONSE
// GET /api/admin/deposits
// =============================================================================
/**
 * Stats grouped by status
 */
export const depositStatsEntrySchema = z.object({
    count: z.number().int(),
    total: z.number().int(),
});
/**
 * Response shape for GET /api/admin/deposits
 */
export const depositsListResponseSchema = z.object({
    deposits: z.array(adminDepositSummarySchema),
    /** Stats keyed by status (pending, paid, completed, etc.) */
    stats: z.record(z.string(), depositStatsEntrySchema),
    /** Current filter applied */
    filter: z.string(),
});
// =============================================================================
// DEPOSIT LOG ENTRY
// Used in detail view: GET /api/admin/deposits/:referenceNumber
// =============================================================================
/**
 * Deposit log entry (from deposit_logs table)
 * Valid log_types: cascade, webhook, checked, receipt_uploaded, force_complete,
 * edit, admin_edit, cancelled, failed
 */
export const depositLogEntrySchema = z.object({
    id: z.number().int(),
    logType: z.string(),
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    photoUrl: z.string().nullable(),
    staffId: z.string().nullable(),
    note: z.string().nullable(),
    previousValues: z.record(z.unknown()).nullable(),
    newValues: z.record(z.unknown()).nullable(),
    result: z.string().nullable(),
    createdAt: z.string(),
});
// =============================================================================
// DEPOSIT DETAIL
// GET /api/admin/deposits/:referenceNumber
// =============================================================================
/**
 * Full deposit detail for admin view
 * Includes main deposit data + user info + logs
 *
 * DB columns: reference_number, user_telegram_id, amount, bonus_amount, bonus_percentage,
 * status, completed_at, provider_hash, provider_transaction_id, usdt_amount, exchange_rate,
 * commission, photo_url, created_at
 */
export const adminDepositDetailSchema = z.object({
    // Core deposit data
    referenceNumber: z.string(),
    telegramId: bigintString,
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    status: depositStatusSchema,
    createdAt: z.string(),
    completedAt: z.string().nullable(),
    // Amount info
    amount: z.number().int(),
    bonusAmount: z.number().int().nullable(),
    bonusPercentage: z.number().int().nullable(),
    // Provider info
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    paymentMethod: z.string().nullable(),
    photoUrl: z.string().nullable(),
    // Financial info (USDT conversion)
    usdtAmount: z.number().nullable(),
    exchangeRate: z.number().nullable(),
    commission: z.number().nullable(),
    // Force complete info (if force completed)
    forceCompleted: z.boolean(),
    forceCompletedAt: z.string().nullable(),
    forceCompletedBy: z.string().nullable(),
    forceCompletedNote: z.string().nullable(),
    // Computed fields
    canForceComplete: z.boolean(),
    ageMinutes: z.number().int(),
    // Logs for history
    logs: z.array(depositLogEntrySchema),
});
// Legacy response shape (for backwards compatibility)
export const depositDetailResponseSchema = adminDepositDetailSchema;
// =============================================================================
// FORCE COMPLETE REQUEST/RESPONSE
// POST /api/admin/deposits/:transactionId/force-complete
// =============================================================================
/**
 * Request body for force-complete
 */
export const forceCompleteRequestSchema = z.object({
    /** Admin note explaining why force-completing (min 15 chars) */
    adminNote: z.string().min(15, 'Admin note is required (minimum 15 characters)'),
});
/**
 * Response shape for force-complete
 */
export const forceCompleteResponseSchema = z.object({
    message: z.string(),
    transactionId: z.string(),
    depositAmount: z.number().int(),
    bonusAmount: z.number().int(),
    totalCredited: z.number().int(),
});
// =============================================================================
// SYNC WITH PROVIDER REQUEST/RESPONSE
// POST /api/admin/deposits/:transactionId/sync
// =============================================================================
/**
 * Response shape for sync with provider
 */
export const syncDepositResponseSchema = z.object({
    referenceNumber: z.string(),
    localStatus: depositStatusSchema,
    providerStatus: z.string(),
    message: z.string(),
});
// =============================================================================
// QUERY PARAMS (for list endpoint)
// =============================================================================
export const depositsListQuerySchema = z.object({
    /** Filter by status: all | pending | completed | cancelled */
    filter: z.enum(['all', 'pending', 'completed', 'cancelled']).optional().default('all'),
    /** Max results to return */
    limit: z.coerce.number().int().positive().optional().default(50),
    /** Search by reference, telegram_id, username */
    search: z.string().optional().default(''),
});
// =============================================================================
// EDIT DEPOSIT REQUEST/RESPONSE
// PUT /api/admin/deposits/:referenceNumber/edit
// =============================================================================
/**
 * Request body for editing deposit financials
 * At least one field must be provided
 */
export const editDepositRequestSchema = z.object({
    /** USDT amount (optional) */
    usdtAmount: z.number().positive().optional(),
    /** Exchange rate (optional) */
    exchangeRate: z.number().positive().optional(),
    /** Commission percentage (optional) */
    commission: z.number().min(0).optional(),
    /** Admin note explaining the edit (optional) */
    note: z.string().optional(),
}).refine((data) => data.usdtAmount !== undefined || data.exchangeRate !== undefined || data.commission !== undefined, { message: 'At least one of usdtAmount, exchangeRate, or commission must be provided' });
/**
 * Response shape for edit deposit
 * Uses camelCase consistently (matches API conventions)
 */
export const editDepositResponseSchema = z.object({
    message: z.string(),
    referenceNumber: z.string(),
    previousValues: z.object({
        usdtAmount: z.number().nullable(),
        exchangeRate: z.number().nullable(),
        commission: z.number().nullable(),
    }),
    newValues: z.object({
        usdtAmount: z.number().optional(),
        exchangeRate: z.number().optional(),
        commission: z.number().optional(),
    }),
});
//# sourceMappingURL=deposits.js.map